'use client';

/**
 * Model Selection Modal Component
 *
 * Modal dialog for selecting a character/model for media generation.
 * Features style filters (Realistic, Anime, Furry) and gender filters (Female, Male, Trans).
 * Uses paginated loading with "Load More" and fetches all when filters are active.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  ChevronDown,
  Search,
  Sparkles,
  Star,
  User,
  X,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';

import {
  type CharacterListItem,
  getMyCharacters,
  getPublicCharacters,
} from '~/lib/server/actions/character-actions';

import { type CharacterOption } from './types';

type StyleFilter = 'all' | 'realistic' | 'anime' | 'furry';
type GenderFilter = 'all' | 'female' | 'male' | 'trans';

const PAGE_SIZE = 24;

interface ModelSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedId: string | null;
  onSelect: (character: CharacterOption) => void;
}

// Style filter options with display names
const STYLE_OPTIONS: { value: StyleFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'realistic', label: 'Realistic' },
  { value: 'anime', label: 'Anime' },
  { value: 'furry', label: 'Furry' },
];

// Gender icon SVG components
function GenderIcon({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  if (gender === 'female') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="12" cy="9" r="6" />
        <line x1="12" y1="15" x2="12" y2="22" />
        <line x1="9" y1="19" x2="15" y2="19" />
      </svg>
    );
  }
  if (gender === 'male') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="5" />
      <line x1="11" y1="16" x2="11" y2="22" />
      <line x1="8" y1="19" x2="14" y2="19" />
      <line x1="19" y1="2" x2="13" y2="8" />
      <polyline points="14 2 19 2 19 7" />
      <line x1="19" y1="14" x2="13" y2="8" />
    </svg>
  );
}

// Gender filter options
const GENDER_OPTIONS: { value: GenderFilter; label: string }[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'trans', label: 'Trans' },
];

// Convert CharacterListItem to CharacterOption
function toCharacterOption(
  char: CharacterListItem,
  isUserCreated: boolean,
): CharacterOption {
  return {
    id: char.id,
    name: char.name,
    slug: char.slug,
    avatarUrl:
      char.avatarUrl ||
      `https://placehold.co/200x250/a855f7/ffffff?text=${encodeURIComponent(char.name)}`,
    style: (char.style as 'realistic' | 'anime') || 'realistic',
    gender: char.gender || undefined,
    isUserCreated,
  };
}

export function ModelSelectionModal({
  isOpen,
  onClose,
  selectedId,
  onSelect,
}: ModelSelectionModalProps) {
  const [activeTab, setActiveTab] = useState<'featured' | 'my-characters'>(
    'featured',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [characters, setCharacters] = useState<CharacterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [styleFilter, setStyleFilter] = useState<StyleFilter>('all');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [tempSelectedId, setTempSelectedId] = useState<string | null>(
    selectedId,
  );
  const [tempSelectedCharacter, setTempSelectedCharacter] =
    useState<CharacterOption | null>(null);

  // Pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [allFetched, setAllFetched] = useState(false);

  // Track if filters are active
  const hasFilters =
    styleFilter !== 'all' ||
    genderFilter !== 'all' ||
    searchQuery.trim() !== '';

  // Reset temp selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedId(selectedId);
    }
  }, [isOpen, selectedId]);

  // Load characters based on active tab
  useEffect(() => {
    async function loadCharacters() {
      if (!isOpen) return;

      setLoading(true);
      setDisplayCount(PAGE_SIZE);
      setAllFetched(false);

      try {
        let mapped: CharacterOption[];

        if (activeTab === 'featured') {
          const result = await getPublicCharacters(PAGE_SIZE, 0);
          mapped = result.characters.map((char) =>
            toCharacterOption(char, false),
          );
          setTotalCount(result.total);
          setAllFetched(mapped.length >= result.total);
        } else {
          const myChars = await getMyCharacters();
          mapped = myChars.map((char) => toCharacterOption(char, true));
          setTotalCount(mapped.length);
          setAllFetched(true);
        }

        setCharacters(mapped);

        // Find and set the currently selected character
        if (selectedId) {
          const selected = mapped.find((c) => c.id === selectedId);
          if (selected) setTempSelectedCharacter(selected);
        }
      } catch (err) {
        console.error('Failed to load characters:', err);
        setCharacters([]);
      } finally {
        setLoading(false);
      }
    }
    loadCharacters();
  }, [isOpen, selectedId, activeTab]);

  // When filters become active, fetch all characters so filtering works across the full set
  useEffect(() => {
    if (!isOpen || activeTab !== 'featured' || !hasFilters || allFetched)
      return;

    async function fetchAll() {
      setLoadingMore(true);
      try {
        const result = await getPublicCharacters(1000, 0);
        const mapped = result.characters.map((char) =>
          toCharacterOption(char, false),
        );
        setCharacters(mapped);
        setTotalCount(result.total);
        setAllFetched(true);
      } catch (err) {
        console.error('Failed to fetch all characters:', err);
      } finally {
        setLoadingMore(false);
      }
    }
    fetchAll();
  }, [isOpen, activeTab, hasFilters, allFetched]);

  // Load more characters (append next page)
  const handleLoadMore = useCallback(async () => {
    if (allFetched || loadingMore) return;

    setLoadingMore(true);
    try {
      const result = await getPublicCharacters(PAGE_SIZE, characters.length);
      const mapped = result.characters.map((char) =>
        toCharacterOption(char, false),
      );
      setCharacters((prev) => [...prev, ...mapped]);
      setTotalCount(result.total);
      setDisplayCount((prev) => prev + PAGE_SIZE);
      if (characters.length + mapped.length >= result.total) {
        setAllFetched(true);
      }
    } catch (err) {
      console.error('Failed to load more characters:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [allFetched, loadingMore, characters.length]);

  // Filter characters
  const filteredCharacters = characters.filter((char) => {
    const matchesSearch = char.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStyle =
      styleFilter === 'all' || (char.style ?? 'realistic') === styleFilter;
    const matchesGender =
      genderFilter === 'all' || char.gender === genderFilter;
    return matchesSearch && matchesStyle && matchesGender;
  });

  // When filters active, show all filtered results; otherwise limit to displayCount
  const visibleCharacters = hasFilters
    ? filteredCharacters
    : filteredCharacters.slice(0, displayCount);

  const showLoadMore =
    !hasFilters && !allFetched && characters.length < totalCount;
  const showShowMore =
    !hasFilters && allFetched && filteredCharacters.length > displayCount;

  const handleShowMore = () => {
    setDisplayCount((prev) => prev + PAGE_SIZE);
  };

  const handleCharacterClick = (character: CharacterOption) => {
    setTempSelectedId(character.id);
    setTempSelectedCharacter(character);
  };

  const handleConfirm = () => {
    if (tempSelectedCharacter) {
      onSelect(tempSelectedCharacter);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="z-[200] mx-2 flex h-[90vh] max-w-4xl flex-col overflow-hidden border-white/10 bg-[#0d0d0d] p-0 sm:mx-auto sm:h-[85vh]">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-white/10 px-4 pt-4 pb-3 sm:px-6 sm:pt-6 sm:pb-4">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white sm:text-xl">
              Select a Model
            </DialogTitle>
          </DialogHeader>

          {/* Tabs and Search */}
          <div className="mt-3 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('featured')}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all sm:gap-2 sm:px-4 ${
                  activeTab === 'featured'
                    ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Star className="h-4 w-4" />
                Featured
              </button>
              <button
                onClick={() => setActiveTab('my-characters')}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all sm:gap-2 sm:px-4 ${
                  activeTab === 'my-characters'
                    ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <User className="h-4 w-4" />
                My Characters
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="focus:border-eros-pink/50 w-full rounded-lg border border-white/10 bg-white/5 py-2 pr-3 pl-9 text-sm text-white placeholder-gray-500 transition-all focus:outline-none sm:w-48"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="mt-3 flex flex-wrap gap-3 sm:mt-4 sm:gap-4">
            {/* Style Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 sm:text-sm">Style:</span>
              <div className="flex gap-1">
                {STYLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setStyleFilter(option.value)}
                    className={`rounded-full px-2 py-1 text-xs transition-all sm:px-3 sm:py-1.5 sm:text-sm ${
                      styleFilter === option.value
                        ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gender Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 sm:text-sm">Gender:</span>
              <div className="flex gap-1">
                {GENDER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      setGenderFilter(
                        genderFilter === option.value ? 'all' : option.value,
                      )
                    }
                    className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs transition-all sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-sm ${
                      genderFilter === option.value
                        ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <GenderIcon
                      gender={option.value}
                      className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Character Grid */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="border-eros-purple h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          ) : visibleCharacters.length === 0 && !loadingMore ? (
            <div className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/5">
                {activeTab === 'my-characters' ? (
                  <Sparkles className="h-10 w-10 text-gray-500" />
                ) : (
                  <Search className="h-10 w-10 text-gray-500" />
                )}
              </div>
              <p className="mb-2 text-lg text-gray-400">
                {activeTab === 'my-characters'
                  ? "You haven't created any characters yet"
                  : 'No characters found'}
              </p>
              {activeTab === 'my-characters' && (
                <a
                  href="/create"
                  className="from-eros-pink to-eros-purple inline-block rounded-lg bg-gradient-to-r px-6 py-2.5 font-medium text-white transition-opacity hover:opacity-90"
                >
                  Create Your First Character
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2.5 min-[480px]:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {visibleCharacters.map((character) => (
                  <ModalModelCard
                    key={character.id}
                    character={character}
                    isSelected={tempSelectedId === character.id}
                    onSelect={() => handleCharacterClick(character)}
                  />
                ))}
              </div>

              {/* Load More button — fetches next page from server */}
              {showLoadMore && (
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-white/5 py-3 text-sm font-medium text-gray-400 transition-all hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  {loadingMore ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {loadingMore
                    ? 'Loading...'
                    : `Load More (${totalCount - characters.length} more)`}
                </button>
              )}

              {/* Show More button — reveals already-fetched characters */}
              {showShowMore && (
                <button
                  onClick={handleShowMore}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-white/5 py-3 text-sm font-medium text-gray-400 transition-all hover:bg-white/10 hover:text-white"
                >
                  <ChevronDown className="h-4 w-4" />
                  Show More ({filteredCharacters.length - displayCount} more)
                </button>
              )}

              {/* Loading indicator when fetching all for filter */}
              {loadingMore && hasFilters && (
                <div className="flex items-center justify-center py-4">
                  <div className="border-eros-purple h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 flex-col items-stretch justify-between gap-3 border-t border-white/10 bg-[#0a0a0a] px-4 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4">
          <div className="text-center text-sm text-gray-400 sm:text-left">
            {tempSelectedCharacter ? (
              <span>
                Selected:{' '}
                <span className="font-medium text-white">
                  {tempSelectedCharacter.name}
                </span>
                {' · '}
                <span className="text-gray-500">
                  Showing {visibleCharacters.length} of{' '}
                  {hasFilters
                    ? `${filteredCharacters.length} matches`
                    : totalCount}
                </span>
              </span>
            ) : (
              <span>
                No model selected
                {' · '}
                <span className="text-gray-500">
                  Showing {visibleCharacters.length} of{' '}
                  {hasFilters
                    ? `${filteredCharacters.length} matches`
                    : totalCount}
                </span>
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg bg-white/5 px-6 py-2.5 font-medium text-white transition-colors hover:bg-white/10 sm:flex-none"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!tempSelectedCharacter}
              className="from-eros-pink to-eros-purple flex-1 rounded-lg bg-gradient-to-r px-6 py-2.5 font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            >
              Select Model
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Model Card for the Modal
interface ModalModelCardProps {
  character: CharacterOption;
  isSelected: boolean;
  onSelect: () => void;
}

function ModalModelCard({
  character,
  isSelected,
  onSelect,
}: ModalModelCardProps) {
  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 ${
        isSelected
          ? 'border-eros-pink shadow-[0_0_20px_rgba(236,72,153,0.5)]'
          : 'border-white/10 hover:border-white/30'
      }`}
    >
      {/* Character Image */}
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={character.avatarUrl}
          alt={character.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      </div>

      {/* Style Badge */}
      <div className="absolute top-2 right-2">
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
            character.style === 'anime'
              ? 'bg-purple-500/80 text-white'
              : character.style === 'furry'
                ? 'bg-orange-500/80 text-white'
                : 'bg-pink-500/80 text-white'
          }`}
        >
          {character.style}
        </span>
      </div>

      {/* Selection Indicator */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute top-2 left-2"
          >
            <div className="bg-eros-pink flex h-6 w-6 items-center justify-center rounded-full shadow-lg">
              <Check className="h-4 w-4 text-white" strokeWidth={3} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Name */}
      <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/90 to-transparent p-2.5">
        <p className="truncate text-sm font-medium text-white">
          {character.name}
        </p>
      </div>
    </motion.button>
  );
}
